import type { DashboardFilters } from '@oxxean/types';
import { useFilters } from '../../hooks/useFilters';
import { MONTHS } from '../../lib/format';
import { Button } from '../../components/primitives';
import { InlineError } from '../../components/states';
import type { CatalogOption } from '../../types/domain';

/**
 * Barra de filtros globales (§25).
 *
 * Afecta a KPI, gráficos, tablas y exportaciones porque todos consumen el mismo
 * objeto `DashboardFilters` del contexto. Los catálogos llegan del backend: no hay
 * ninguna lista de empresas ni de áreas escrita en el frontend.
 */
const RISK_OPTIONS: ReadonlyArray<{ value: NonNullable<DashboardFilters['risk']>; label: string }> = [
  { value: 'LOW', label: 'Bajo' },
  { value: 'MEDIUM', label: 'Medio' },
  { value: 'HIGH', label: 'Alto' },
  { value: 'CRITICAL', label: 'Crítico' },
];

export function GlobalFilters(): JSX.Element {
  const {
    filters,
    setFilter,
    clearFilters,
    activeCount,
    options,
    optionsLoading,
    optionsError,
    reloadOptions,
  } = useFilters();

  return (
    <section className="filters" aria-label="Filtros globales">
      {optionsError !== null && optionsError !== undefined && (
        <InlineError error={optionsError} onRetry={reloadOptions} />
      )}

      <div className="filters__grid">
        <NumberSelect
          label="Año"
          value={filters.year}
          options={options?.years ?? []}
          disabled={optionsLoading}
          onChange={(value) => setFilter('year', value)}
        />

        <label>
          <span className="filters__label">Mes</span>
          <select
            className="select"
            value={filters.month ?? ''}
            onChange={(event) =>
              setFilter('month', event.target.value === '' ? undefined : Number(event.target.value))
            }
          >
            <option value="">Todos</option>
            {MONTHS.map((month) => (
              <option key={month.value} value={month.value}>
                {month.label}
              </option>
            ))}
          </select>
        </label>

        <label>
          <span className="filters__label">Rango desde</span>
          <input
            className="input"
            type="date"
            value={filters.dateFrom ?? ''}
            max={filters.dateTo}
            onChange={(event) => setFilter('dateFrom', event.target.value || undefined)}
          />
        </label>

        <label>
          <span className="filters__label">Rango hasta</span>
          <input
            className="input"
            type="date"
            value={filters.dateTo ?? ''}
            min={filters.dateFrom}
            onChange={(event) => setFilter('dateTo', event.target.value || undefined)}
          />
        </label>

        <OptionSelect
          label="Empresa"
          value={filters.companyId}
          options={options?.companies}
          disabled={optionsLoading}
          onChange={(value) => setFilter('companyId', value)}
        />
        <OptionSelect
          label="Área"
          value={filters.areaId}
          options={options?.areas}
          disabled={optionsLoading}
          onChange={(value) => setFilter('areaId', value)}
        />
        <OptionSelect
          label="Centro de costo"
          value={filters.costCenterId}
          options={options?.costCenters}
          disabled={optionsLoading}
          onChange={(value) => setFilter('costCenterId', value)}
        />
        <OptionSelect
          label="Sede"
          value={filters.locationId}
          options={options?.locations}
          disabled={optionsLoading}
          onChange={(value) => setFilter('locationId', value)}
        />
        <OptionSelect
          label="Cargo"
          value={filters.roleName}
          options={options?.jobRoles}
          disabled={optionsLoading}
          useLabelAsValue
          onChange={(value) => setFilter('roleName', value)}
        />
        <OptionSelect
          label="Contrato"
          value={filters.contractType}
          options={options?.contractTypes}
          disabled={optionsLoading}
          useLabelAsValue
          onChange={(value) => setFilter('contractType', value)}
        />
        <OptionSelect
          label="Estado"
          value={filters.employmentStatus}
          options={options?.employmentStatuses}
          disabled={optionsLoading}
          useLabelAsValue
          onChange={(value) => setFilter('employmentStatus', value)}
        />

        <label>
          <span className="filters__label">Riesgo</span>
          <select
            className="select"
            value={filters.risk ?? ''}
            onChange={(event) =>
              setFilter(
                'risk',
                event.target.value === ''
                  ? undefined
                  : (event.target.value as NonNullable<DashboardFilters['risk']>),
              )
            }
          >
            <option value="">Todos</option>
            {RISK_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="filters__foot">
        <Button size="sm" variant="ghost" icon="close" onClick={clearFilters} disabled={activeCount === 0}>
          Limpiar filtros
        </Button>
        <span aria-live="polite">
          {activeCount === 0
            ? 'Sin filtros aplicados: se muestra el ámbito completo autorizado.'
            : `${activeCount} filtro(s) aplicado(s). Afectan a indicadores, gráficos, tablas y exportaciones.`}
        </span>
        {optionsLoading && <span className="muted">Cargando catálogos…</span>}
      </div>
    </section>
  );
}

function OptionSelect({
  label,
  value,
  options,
  disabled,
  onChange,
  useLabelAsValue = false,
}: {
  label: string;
  value: string | undefined;
  options: CatalogOption[] | undefined;
  disabled: boolean;
  onChange: (value: string | undefined) => void;
  /** Algunos filtros del contrato viajan por nombre (cargo, contrato, estado). */
  useLabelAsValue?: boolean;
}): JSX.Element {
  const list = options ?? [];
  return (
    <label>
      <span className="filters__label">{label}</span>
      <select
        className="select"
        value={value ?? ''}
        disabled={disabled || list.length === 0}
        onChange={(event) => onChange(event.target.value === '' ? undefined : event.target.value)}
      >
        <option value="">Todas</option>
        {list.map((option) => (
          <option key={option.id} value={useLabelAsValue ? option.label : option.id}>
            {option.label}
          </option>
        ))}
      </select>
    </label>
  );
}

function NumberSelect({
  label,
  value,
  options,
  disabled,
  onChange,
}: {
  label: string;
  value: number | undefined;
  options: number[];
  disabled: boolean;
  onChange: (value: number | undefined) => void;
}): JSX.Element {
  return (
    <label>
      <span className="filters__label">{label}</span>
      <select
        className="select"
        value={value ?? ''}
        disabled={disabled || options.length === 0}
        onChange={(event) => onChange(event.target.value === '' ? undefined : Number(event.target.value))}
      >
        <option value="">Todos</option>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>
    </label>
  );
}
